import { clone } from "ramda";

import { type BlastHit } from "../../graphql.js";

export interface IRenderableHit extends BlastHit {
  partialHspList?: boolean;
  totalHspCount?: number;
  first?: number;
  last?: number;
}

export interface IPaginationMap {
  getHitsForPage: (page: number) => IRenderableHit[];
  getFirstHspForPage: (page: number, hitNum: number) => number;
  isFirstHspForPage: (page: number, hitNum: number, hspNum: number) => boolean;
  getPageOf: (hit: number, hsp?: number) => number;
  getNumberOfPages: () => number;
}

const MAX_HSPS_PER_PAGE = 20;

class AlnPaginationModel implements IPaginationMap {
  private hsp2page = new Array<number[]>();
  private page2hits = new Array<IRenderableHit[]>();

  constructor(hits: BlastHit[]) {
    this.partitionHSPs(hits);
  }

  getHitsForPage(page: number): IRenderableHit[] {
    return this.page2hits[page - 1];
  }

  getFirstHspForPage(page: number, hitNum: number): number {
    return this.hsp2page[hitNum - 1].findIndex((p: number) => p === page - 1) + 1;
  }

  isFirstHspForPage(page: number, hitNum: number, hspNum: number): boolean {
    return this.getFirstHspForPage(page, hitNum) === hspNum && this.getHitsForPage(page)[0].rank === hitNum;
  }

  getPageOf(hitNum: number, hspNum?: number): number {
    if (!hitNum) {
      return 1;
    }
    return this.hsp2page[hitNum - 1][hspNum ? hspNum - 1 : 0] + 1;
  }

  getNumberOfPages() {
    return this.page2hits.length;
  }

  private partitionHSPs = (hits: BlastHit[]): void => {
    let page = 0;
    let hspCount = 0;
    this.page2hits.push([]);
    hits.map((hit) => {
      if (hit.hsps.length > MAX_HSPS_PER_PAGE) {
        // start a new page for the split hit if this is not already a blanc page
        if (hspCount > 0) {
          ++page;
          this.page2hits.push([]);
        }
        this.hsp2page[hit.rank - 1] = new Array<number>(hit.hsps.length);
        this.splitHit(hit).map((rHit, index, array) => {
          rHit.hsps.map((hsp) => (this.hsp2page[rHit.rank - 1][hsp.rank - 1] = page));
          this.page2hits[page].push(rHit);
          if (index < array.length - 1) {
            ++page;
            this.page2hits.push([]);
          } else {
            hspCount = rHit.hsps.length;
          }
        });
      } else {
        hspCount += hit.hsps.length;
        if (hspCount > MAX_HSPS_PER_PAGE) {
          ++page;
          this.page2hits.push([]);
          hspCount = 0;
        }
        this.hsp2page[hit.rank - 1] = new Array<number>(hit.hsps.length);
        hit.hsps.map((hsp) => (this.hsp2page[hit.rank - 1][hsp.rank - 1] = page));
        const rHit: IRenderableHit = { ...hit };
        rHit.partialHspList = false;
        rHit.totalHspCount = hit.hsps.length;
        this.page2hits[page].push(rHit);
      }
    });
  };

  private splitHit(hit: BlastHit): IRenderableHit[] {
    const blocks = Math.ceil(hit.hsps.length / MAX_HSPS_PER_PAGE);
    const r = new Array<IRenderableHit>(blocks);
    for (let i = 0; i < blocks; ++i) {
      const rHit: IRenderableHit = clone(hit);
      rHit.totalHspCount = hit.hsps.length;
      rHit.partialHspList = true;
      rHit.first = i * MAX_HSPS_PER_PAGE + 1;
      rHit.last = Math.min((i + 1) * MAX_HSPS_PER_PAGE, hit.hsps.length);
      rHit.hsps = rHit.hsps.slice(rHit.first - 1, rHit.last);
      r[i] = rHit;
    }
    return r;
  }
}

export default AlnPaginationModel;
